import type { FC } from 'react'
import {
	Flex,
	Breadcrumb as ChakraBreadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Text,
	chakra,
} from '@chakra-ui/react'
import type {
	LocationResult,
	LocationBreadCrumb,
} from '@alexanderathoodly/data-models'
import { Link, useLocation } from 'react-router-dom'
import { RightArrowIcn, PinIcn } from '@alexanderathoodly/ui-library'
import { useTranslation } from 'react-i18next'

interface BreadcrumProps {
	location: LocationResult
}

export const Breadcrumb: FC<BreadcrumProps> = ({ location }) => {
	const { t } = useTranslation()

	const path = useLocation()
	const getBreadCrumbUrl = (breadCrumb: LocationBreadCrumb) => {
		if (breadCrumb.url) {
			return `/${breadCrumb.url}${path.search}`
		}
		const search = new URLSearchParams(path.search)
		search.append('area', `${breadCrumb.osm_type}${breadCrumb.osm_id}`)
		return `?${search.toString()}`
	}
	const pinStyle = {
		outer: {
			fill: 'blue.500',
			stroke: 'blue.500',
		},
		inner: { fill: 'white' },
	}
	const breadCrumbAreas = [location.breadcrumbs.city, location.breadcrumbs.area]

	return (
		<Flex mt={2}>
			<PinIcn h={4} w={4} outer={pinStyle.outer} inner={pinStyle.inner} />
			<ChakraBreadcrumb
				color="gray.600"
				fontWeight={400}
				spacing={1}
				separator={<RightArrowIcn color="black" h={2} strokeWidth={4} />}
				ml={2}
				itemScope
				itemType="https://schema.org/BreadcrumbList"
			>
				{breadCrumbAreas.map((breadCrumb, i) => {
					return breadCrumb ? (
						<BreadcrumbItem
							key={breadCrumb.osm_id}
							itemProp="itemListElement"
							itemScope
							itemType="https://schema.org/ListItem"
						>
							<BreadcrumbLink
								fontWeight={400}
								as={Link}
								to={`${t('/lediga-kontor', {
									ns: 'url',
								})}${getBreadCrumbUrl(breadCrumb)}`}
								itemProp="item"
							>
								<chakra.span itemProp="name">
									<chakra.span
										display={{
											base: 'none',
											lg: i == 0 ? 'inline' : 'none',
										}}
									>
										{t('Kontor i')}{' '}
									</chakra.span>
									{breadCrumb.name}
								</chakra.span>
								<meta itemProp="position" content={(i + 1).toString()} />
							</BreadcrumbLink>
						</BreadcrumbItem>
					) : null
				})}
				<BreadcrumbItem isCurrentPage itemProp="item">
					<Text>
						{location.address.street} {location.address.locator}
					</Text>
					<meta itemProp="name" content={location.name} />
					<meta
						itemProp="position"
						content={(breadCrumbAreas.length + 1).toString()}
					/>
				</BreadcrumbItem>
			</ChakraBreadcrumb>
		</Flex>
	)
}
